import { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

interface FullPageLogoLoaderProps {
  label?: string
  visible?: boolean
}

// Branded/animated — for route-level or first-load screens. For a quick
// in-flight action on a screen the user is already looking at (save/print/PDF),
// use the plainer `BusyOverlay` instead.

const FRAMES: { icon: string; quote: string }[] = [
  { icon: '✏️', quote: 'Everything you need, delivered at your doorstep' },
  { icon: '📒', quote: 'Crafting your stationery moment…' },
  { icon: '📐', quote: 'Lining up the perfect order' },
  { icon: '🖊️', quote: 'Penning down your wishlist' },
  { icon: '📎', quote: 'Clipping the details together' },
  { icon: '📚', quote: 'Stacking up something special' },
  { icon: '🎨', quote: 'Painting your day a little brighter' },
  { icon: '✂️', quote: 'Cutting out the wait — almost there' },
]

const LoaderInner = ({ label }: { label?: string }) => {
  const [index, setIndex] = useState(0)

  useEffect(() => {
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % FRAMES.length)
    }, 600)
    return () => clearInterval(id)
  }, [])

  const { icon, quote } = FRAMES[index]
  const message = label ?? quote

  return (
    <motion.div
      initial={{ y: '100%', borderTopLeftRadius: 48, borderTopRightRadius: 48 }}
      animate={{ y: 0, borderTopLeftRadius: 0, borderTopRightRadius: 0 }}
      exit={{ y: '100%', borderTopLeftRadius: 48, borderTopRightRadius: 48 }}
      transition={{ duration: 0.12, ease: [0.22, 1, 0.36, 1] }}
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center gap-6 bg-coral-500 px-8 overflow-hidden"
    >
      <div className="absolute left-4 top-4 flex items-center gap-2 sm:left-8 sm:top-6">
        <img
          src="https://assets-statiomart.s3.ap-south-1.amazonaws.com/statiomart.png"
          alt=""
          className="h-7 w-7 rounded-lg"
        />
        <span className="text-lg font-bold tracking-tight text-white">StatioMart</span>
      </div>

      <div className="flex h-20 w-20 items-center justify-center">
        <AnimatePresence mode="wait" initial={false}>
          <motion.span
            key={icon}
            initial={{ opacity: 0, y: 10, scale: 0.85 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.85 }}
            transition={{ duration: 0.12, ease: [0.22, 1, 0.36, 1] }}
            className="text-6xl"
            aria-hidden
          >
            {icon}
          </motion.span>
        </AnimatePresence>
      </div>

      <div className="h-12 max-w-xs overflow-hidden text-center">
        <AnimatePresence mode="wait" initial={false}>
          <motion.p
            key={message}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            className="text-base font-medium leading-relaxed text-white/80"
          >
            {message}
          </motion.p>
        </AnimatePresence>
      </div>
    </motion.div>
  )
}

export const FullPageLogoLoader = ({ label, visible = true }: FullPageLogoLoaderProps) => (
  <AnimatePresence>{visible && <LoaderInner label={label} />}</AnimatePresence>
)

export default FullPageLogoLoader
